import Echo from 'laravel-echo';

import { Common } from '@sv-support-sdk/shared-types';
import { toCamelCaseRecursive } from '@sv-support-sdk/utils/src/helpers/string';

import { ticketsActions } from '../../../config';
import { TThunk } from '../../../';

export const markTicketAsResolved =
  (ticketId: string, hash: string): TThunk =>
  async (dispatch, _, api) => {
    dispatch(ticketsActions.setSelectedTicketLoading(true));

    try {
      const data = await api.tickets.updateTicket(ticketId, { hash }, { resolved: 1 });

      dispatch(ticketsActions.updateSelectedTicket(data));
    } finally {
      dispatch(ticketsActions.setSelectedTicketLoading(false));
    }
  };

export const markMessagesAsRead =
  (ticketId: number, hash: string, read: number): TThunk =>
  async (dispatch, _, api) => {
    dispatch(ticketsActions.setLoading({ key: 'markMessagesAsRead', value: true }));

    try {
      const data = await api.tickets.markMessagesAsRead(ticketId, { hash }, { read });

      dispatch(ticketsActions.updateSelectedTicket(data));
      dispatch(ticketsActions.removeMessagesFromUnread(ticketId));
    } finally {
      dispatch(ticketsActions.setLoading({ key: 'markMessagesAsRead', value: false }));
    }
  };

export const fetchUnreadTickets = (): TThunk => async (dispatch, _, api) => {
  dispatch(ticketsActions.setLoading({ key: 'fetchUnreadTickets', value: true }));

  try {
    const { data } = await api.tickets.fetchUnreadTicketsMessages();

    dispatch(ticketsActions.setUnreadTicketMessages(data.ticketMessages));
    dispatch(ticketsActions.setTicketsMeta({ hash: data.hash }));
  } finally {
    dispatch(ticketsActions.setLoading({ key: 'fetchUnreadTickets', value: false }));
  }
};

export const addAttachmentToMessage =
  (attachment: { file: string; name: string; size: number }, showError?: () => void): TThunk =>
  async (dispatch, _, api) => {
    dispatch(ticketsActions.setLoading({ key: 'attachments', value: true }));

    try {
      const data = await api.tickets.saveAttachment(attachment);
      dispatch(ticketsActions.setReplyAttachments(data));
    } catch {
      if (showError) {
        dispatch(showError());
      }
    } finally {
      dispatch(ticketsActions.setLoading({ key: 'attachments', value: false }));
    }
  };

export const removeAttachmentFromMessage =
  (attachmentId: number): TThunk =>
  (dispatch) => {
    dispatch(ticketsActions.deleteReplyAttachment(attachmentId));
  };

export const onMessageCreated =
  ({ message }: { message: Common.Tickets.IMessage }): TThunk =>
  async (dispatch) => {
    if (message.author?.type === 'Staff') {
      dispatch(ticketsActions.addMessageToTicketList(message));
    }
  };

export const connectToSocketAndSubscribe =
  (userId: string, hash: string, socket: () => Promise<Echo>, company: string): TThunk =>
  async (dispatch) => {
    const socketConnection: Echo = await socket();

    const getTicketsChannel = (userId: string, hash: string) =>
      `support.${company}.${userId}.${hash}`;

    socketConnection
      .private(getTicketsChannel(userId, hash))
      .listen('.ticket.message.created', (data: { message: Common.Tickets.IMessage }) =>
        dispatch(onMessageCreated(toCamelCaseRecursive(data))),
      );
  };
